/* eslint-disable react/no-unused-prop-types */
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircleIcon, EyeIcon, PauseIcon, PlayIcon } from "lucide-react";
import type { Dispatch, SetStateAction } from "react";
import { toast } from "react-hot-toast";
import { FormattedMessage, useIntl } from "react-intl";
import { Menu, MenuActionButtonItem } from "components/core/menu";
import ActionsDropdown from "components/discussions/ActionsDropdown";
import StarButton from "components/engagement/star/StarButton";
import type { IFinancialsTemplatesQuery } from "components/financials/modern/graphql/__generated__/FinancialsTemplatesQuery.generated";
import type { IFinancialsTemplateFieldsFragment } from "components/financials/modern/graphql/fragments/__generated__/FinancialsTemplateFieldsFragment.generated";
import PermanentlyDeleteTemplateButton from "components/financials/modern/templates/PermanentlyDeleteTemplateButton";
import ToggleArchivedButton from "components/financials/modern/templates/ToggleArchivedButton";
import { useTemplateActions } from "components/financials/modern/templates/hooks/useTemplateActions";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { Button } from "components/ui/Button";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { IFinancialsTemplateNamespace, IFinancialsTemplateVisibility } from "types/graphqlTypes";
import { TemplatesEnum } from "components/financials/modern/templates/types";
import type {
  IFinancialsSheetQuery,
  IFinancialsSheetQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsSheetQuery.generated";
import type { ApolloQueryResult } from "@apollo/client";

interface TemplatesListProps {
  children?: React.ReactNode;
  inheritedTemplate: IFinancialsTemplateFieldsFragment | null;
  templates: IFinancialsTemplatesQuery["financialsTemplates"]["nodes"];
  tab: TemplatesEnum;
  isLoading: boolean;
  hasNextPage?: boolean;
  systemTemplate: IFinancialsTemplateFieldsFragment | null;
  renderingTemplate: IFinancialsTemplateFieldsFragment | null;
  onEditClick: (template: IFinancialsTemplatesQuery["financialsTemplates"]["nodes"][number]) => void;
  onToggleSheetOpen?: () => void;
  onRefetch?: () => void;
  onRefetchStarredTemplates?: () => void;
  onRefetchFinancialsTemplate: (
    newVariables: Partial<IFinancialsSheetQueryVariables>,
  ) => Promise<ApolloQueryResult<IFinancialsSheetQuery>>;
  onFetchMore?: () => void;
  onApplyTemplate?: (template: IFinancialsTemplatesQuery["financialsTemplates"]["nodes"][number] | null) => void;
  setSelectedTab: Dispatch<SetStateAction<TemplatesEnum>>;
}
export default function TemplatesList({
  children,
  inheritedTemplate,
  templates,
  hasNextPage,
  isLoading,
  tab,
  onEditClick,
  onToggleSheetOpen,
  onRefetch,
  onRefetchStarredTemplates,
  onRefetchFinancialsTemplate,
  onFetchMore,
  onApplyTemplate,
  renderingTemplate,
}: TemplatesListProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const {
    addFinancialsTemplate,
    toggleArchivedOnFinancialsTemplate,
    changeFinancialsTemplateVisibility,
    permanentlyDeleteFinancialsTemplate,
    permanentlyDeleteArchivedTemplates,
    setAsDefaultFinancialsTemplate,
    addStar,
    removeStar,
    loading: {
      archivedOnFinancialsTemplateLoading,
      addFinancialsTemplateLoading,
      changeVisibilityLoading,
      permanentlyDeleteLoading,
      loadingSetAsDefaultFinancialsTemplate,
      isAddingStar,
      isRemovingStar,
    },
  } = useTemplateActions();

  const isMutating = isAddingStar || isRemovingStar;
  const isMyTemplateTab = tab === TemplatesEnum.MyTemplates;
  const deleteAllConfirmMessage = intl.formatMessage({
    defaultMessage: "Tem certeza que deseja apagar todos os templates?",
    id: "IzQS/T",
  });

  return (
    <div className="space-y-6 pb-6">
      <div className="w-full space-y-4">
        {children}

        {tab === TemplatesEnum.ArchivedTemplates && templates.length > 0 && (
          <Button
            size="full"
            disabled={templates.length === 0}
            variant="ghost"
            className="bg-red-50 text-red-600 hover:opacity-80 dark:bg-red-800/20 dark:text-red-200"
            onClick={() => {
              // eslint-disable-next-line no-restricted-globals, no-alert
              const didConfirm = confirm(deleteAllConfirmMessage);
              if (didConfirm) {
                permanentlyDeleteArchivedTemplates({
                  onCompleted: () => {
                    toast.success(
                      intl.formatMessage({
                        id: "8U3PAs",
                        defaultMessage: "Templates deletados permanentemente",
                      }),
                      {
                        position: "bottom-right",
                        duration: 2200,
                      },
                    );
                    onRefetch?.();
                  },
                });
              }
            }}
          >
            <FormattedMessage defaultMessage="Apagar todos" id="7aGqr0" />
          </Button>
        )}

        {isLoading ? (
          <LoadingSkeleton count={4} />
        ) : (
          <AnimatePresence>
            <ul
              className={classNames(
                "divide-y divide-gray-900/10 overflow-hidden rounded-xl bg-white ring-1 ring-gray-900/10",
                "dark:bg-slate-800",
                { "bg-slate-50": templates.length === 0 },
              )}
            >
              {templates.length === 0 && (
                <li>
                  <p className="p-4 text-center text-sm tracking-wider text-gray-600 dark:text-slate-300">
                    <FormattedMessage defaultMessage="Nenhum template encontrado." id="2iuNVE" />
                  </p>
                </li>
              )}

              {templates.filter(notUndefinedOrNull).map((template) => {
                const isAppliedTemplate = renderingTemplate ? renderingTemplate.objectId === template.objectId : false;
                const isArchived = !!template.archivedAt;
                const isPrivate = template.visibility === IFinancialsTemplateVisibility.Private;
                const isDefault =
                  !!inheritedTemplate &&
                  template.overrideCriteria?.forPlatformVertical === inheritedTemplate.vertical &&
                  inheritedTemplate.templateCodes.every((templateCode) => {
                    return template.overrideCriteria?.forTemplateCodes?.includes(templateCode);
                  });
                const canSetAsDefault = inheritedTemplate
                  ? inheritedTemplate?.templateCodes.every((templateCode) => {
                      return template.templateCodes?.includes(templateCode);
                    })
                  : false;

                async function setAsDefaultTemplate() {
                  if (loadingSetAsDefaultFinancialsTemplate || addFinancialsTemplateLoading) {
                    return;
                  }

                  if (!inheritedTemplate) {
                    return;
                  }

                  await setAsDefaultFinancialsTemplate({
                    variables: {
                      objectId: template.objectId,
                      input: {
                        platformVertical: inheritedTemplate.vertical,
                      },
                    },
                  });
                  onRefetchFinancialsTemplate({
                    financialsTemplate: null,
                  });

                  onRefetch?.();
                }

                return (
                  <motion.li
                    key={template.objectId}
                    id={template.objectId}
                    className={classNames(
                      "relative flex items-center justify-between gap-x-12 px-4 py-4",
                      "hover:bg-gray-50 dark:hover:bg-slate-900",
                      { "bg-blue-50/50 dark:bg-slate-900/50": isAppliedTemplate },
                    )}
                    initial={{
                      opacity: 0,
                    }}
                    animate={{
                      opacity: 1,
                    }}
                    exit={{
                      opacity: 0,
                    }}
                  >
                    <div className="flex min-w-0 gap-x-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 md:gap-2 md:gap-y-1">
                          <h3 className="max-w-48 truncate break-all text-base font-semibold text-gray-700 dark:text-slate-100 sm:max-w-md">
                            {template.name}
                          </h3>

                          {isDefault && (
                            <span
                              className={classNames(
                                "inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-xs font-bold dark:bg-sky-600/20 dark:text-sky-300",
                                "bg-sky-100 text-sky-600",
                              )}
                            >
                              <FormattedMessage defaultMessage="Padrão" id="fufvkr" />
                            </span>
                          )}

                          {!isPrivate && tab === TemplatesEnum.MyTemplates && (
                            <SimpleTextTooltip
                              content={intl.formatMessage({
                                defaultMessage: "Template público",
                                id: "g3vh+n",
                                description: "Eye icon tooltip about public templates",
                              })}
                            >
                              <EyeIcon
                                className="h-4 w-4 text-orange-600"
                                strokeWidth={2.5}
                                absoluteStrokeWidth={true}
                              />
                            </SimpleTextTooltip>
                          )}

                          {isAppliedTemplate && <CheckCircleIcon className="h-4 w-4 stroke-blue-600" />}

                          {!template.isNativeTemplate && (
                            <p className="inline-flex w-full space-x-1 text-xs italic">
                              <span className="font-medium tracking-wide text-gray-500 dark:text-slate-400">
                                <FormattedMessage
                                  defaultMessage="por"
                                  id="RRJqEm"
                                  description="The author of the template in the templates page"
                                />
                              </span>

                              {template.isFundamenteiTemplate && (
                                <strong className="font-bold tracking-normal text-brand dark:text-blue-300">
                                  Fundamentei
                                </strong>
                              )}

                              {template.namespace === IFinancialsTemplateNamespace.User && template.createdBy && (
                                <strong
                                  title={template.createdBy.fullName}
                                  className="max-w-48 truncate font-bold tracking-wide text-slate-500 dark:text-slate-400"
                                >
                                  {template.createdBy.fullName}
                                </strong>
                              )}
                            </p>
                          )}
                        </div>

                        <p className="mt-3 line-clamp-3 break-all text-sm font-normal leading-6 tracking-wide text-gray-500 dark:text-slate-400">
                          {template.description}
                        </p>
                      </div>
                    </div>

                    <div className="hidden md:flex">
                      {tab !== TemplatesEnum.ArchivedTemplates ? (
                        <span className="inline-flex h-10 flex-nowrap items-center rounded-xl">
                          <StarButton
                            starrable={template}
                            disabled={!selfie}
                            onAddStar={onRefetchStarredTemplates}
                            onRemoveStar={onRefetchStarredTemplates}
                          />

                          <Button
                            type="button"
                            variant="ghost"
                            className={classNames(
                              "group relative -ml-px h-10 w-12 items-center bg-slate-50/60 p-2",
                              "rounded-none border  border-slate-200",
                              "dark:border-slate-950 dark:bg-slate-700",
                              "focus-visible:outline-none",
                              "focus-visible:ring-1 focus-visible:ring-offset-1",
                              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                              "focus-visible:border-transparent focus-visible:bg-gray-50",
                              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                              "disabled:cursor-not-allowed disabled:opacity-50",
                            )}
                            onClick={() => {
                              onApplyTemplate?.(isAppliedTemplate ? null : template);

                              if (onToggleSheetOpen) {
                                onToggleSheetOpen();
                              }
                            }}
                          >
                            {isAppliedTemplate ? (
                              <PauseIcon
                                strokeWidth={2}
                                className={classNames(
                                  "h-5 w-5 fill-sky-500 stroke-sky-500 dark:fill-sky-300 dark:stroke-sky-300",
                                  "group-hover:opacity-80",
                                )}
                              />
                            ) : (
                              <PlayIcon
                                strokeWidth={2}
                                absoluteStrokeWidth={true}
                                className={classNames(
                                  "h-4 w-4 fill-slate-50 stroke-slate-500 dark:fill-slate-300 dark:stroke-slate-300",
                                  "group-hover:fill-sky-50 group-hover:stroke-sky-600",
                                  "dark:group-hover:fill-sky-300 dark:group-hover:stroke-sky-300",
                                )}
                              />
                            )}
                          </Button>

                          <ActionsDropdown
                            className={classNames(
                              "group relative -ml-px h-10 w-12 items-center bg-slate-50/60 p-2",
                              "rounded-none rounded-r-xl border border-slate-200",
                              "dark:border-slate-950 dark:bg-slate-700",
                              "hover:bg-slate-50/60",
                              "focus-visible:outline-none",
                              "focus-visible:ring-1 focus-visible:ring-offset-1",
                              "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                              "focus-visible:border-transparent focus-visible:bg-gray-50",
                              "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                              "disabled:cursor-not-allowed disabled:opacity-50",
                              { "pointer-events-none opacity-50": !selfie },
                            )}
                            align="end"
                          >
                            {({ closeOnClick }) => (
                              <ul
                                className={classNames(
                                  "m-0 grid min-w-[180px] max-w-[280px] list-none gap-y-1 rounded-xl px-3 py-1.5",
                                  "dark:border-transparent",
                                )}
                              >
                                {!isArchived && template.viewerCanEdit && (
                                  <MenuActionButtonItem
                                    variant="primary"
                                    onClick={closeOnClick(() => onEditClick(template))}
                                  >
                                    <FormattedMessage defaultMessage="Editar" id="VGHwB5" />
                                  </MenuActionButtonItem>
                                )}

                                {template.viewerCanCopy && (
                                  <MenuActionButtonItem
                                    variant="primary"
                                    disabled={addFinancialsTemplateLoading}
                                    onClick={() => {
                                      addFinancialsTemplate({
                                        variables: {
                                          input: {
                                            templateCodes: template.templateCodes,
                                            name: `Cópia de ${template.name}`,
                                            description: template.description,
                                            indicators: template.indicators.map(({ objectId }) => objectId),
                                            vertical: template.vertical,
                                            visibility: IFinancialsTemplateVisibility.Private,
                                            countryCode: template.countryCode,
                                            dataSource: template.dataSource,
                                            betterSuitedFor: [],
                                            overrideCriteria: null,
                                          },
                                        },
                                        onCompleted: onRefetch,
                                      });
                                    }}
                                  >
                                    {isMyTemplateTab ? (
                                      <FormattedMessage
                                        defaultMessage="Duplicar"
                                        id="QdMuI+"
                                        description="Duplicate template action"
                                      />
                                    ) : (
                                      <FormattedMessage
                                        defaultMessage="Copiar Template"
                                        id="fG9Ivd"
                                        description="Copy template action"
                                      />
                                    )}
                                  </MenuActionButtonItem>
                                )}

                                {inheritedTemplate && !isDefault && canSetAsDefault && (
                                  <MenuActionButtonItem
                                    variant="primary"
                                    disabled={
                                      loadingSetAsDefaultFinancialsTemplate ||
                                      addFinancialsTemplateLoading ||
                                      !!isDefault
                                    }
                                    onClick={() => setAsDefaultTemplate()}
                                  >
                                    <FormattedMessage
                                      defaultMessage="Definir como padrão"
                                      id="wWS2iq"
                                      description="Set template as default button"
                                    />
                                  </MenuActionButtonItem>
                                )}

                                {template.viewerCanChangeVisibility && (
                                  <MenuActionButtonItem
                                    variant="primary"
                                    disabled={changeVisibilityLoading}
                                    onClick={() => {
                                      changeFinancialsTemplateVisibility({
                                        variables: {
                                          objectId: template.objectId,
                                          input: {
                                            visibility:
                                              template.visibility === IFinancialsTemplateVisibility.Private
                                                ? IFinancialsTemplateVisibility.Public
                                                : IFinancialsTemplateVisibility.Private,
                                          },
                                        },
                                      });
                                    }}
                                  >
                                    {isPrivate ? (
                                      <FormattedMessage
                                        defaultMessage="Deixar Público"
                                        id="/h/UGL"
                                        description="Template visibility setting"
                                      />
                                    ) : (
                                      <FormattedMessage
                                        description="Template visibility setting"
                                        defaultMessage="Deixar Privado"
                                        id="IYh7NR"
                                      />
                                    )}
                                  </MenuActionButtonItem>
                                )}

                                {template.viewerCanArchive && (
                                  <MenuActionButtonItem
                                    variant="danger"
                                    disabled={archivedOnFinancialsTemplateLoading}
                                    onClick={() =>
                                      toggleArchivedOnFinancialsTemplate({
                                        variables: {
                                          objectId: template.objectId,
                                        },
                                        onCompleted: onRefetch,
                                      })
                                    }
                                  >
                                    {isArchived ? (
                                      <FormattedMessage
                                        defaultMessage="Restaurar"
                                        id="LTm6tU"
                                        description="Restore template action"
                                      />
                                    ) : (
                                      <FormattedMessage
                                        defaultMessage="Arquivar"
                                        id="ToNV1d"
                                        description="Archive template action"
                                      />
                                    )}
                                  </MenuActionButtonItem>
                                )}

                                {isArchived && template.viewerCanDelete && (
                                  <MenuActionButtonItem
                                    variant="primary"
                                    disabled={permanentlyDeleteLoading}
                                    onClick={() => {
                                      permanentlyDeleteFinancialsTemplate({
                                        variables: {
                                          objectId: template.objectId,
                                        },
                                        onCompleted: onRefetch,
                                      });
                                    }}
                                  >
                                    <FormattedMessage defaultMessage="Deletar" id="XPy3Mz" />
                                  </MenuActionButtonItem>
                                )}
                              </ul>
                            )}
                          </ActionsDropdown>
                        </span>
                      ) : (
                        <span className="inline-flex h-10 flex-nowrap items-center rounded-xl">
                          {template.viewerCanArchive && (
                            <ToggleArchivedButton template={template} onToggleArchived={onRefetch} />
                          )}

                          {isArchived && template.viewerCanDelete && (
                            <PermanentlyDeleteTemplateButton template={template} onPermanentlyDelete={onRefetch} />
                          )}
                        </span>
                      )}
                    </div>

                    <ActionsDropdown
                      align="end"
                      className={classNames("flex md:hidden", {
                        "pointer-events-none opacity-50": !selfie,
                      })}
                    >
                      {({ closeOnClick }) => (
                        <Menu>
                          <MenuActionButtonItem
                            variant="primary"
                            disabled={isMutating}
                            onClick={() => {
                              if (isMutating) {
                                return;
                              }

                              if (template.viewerHasStarred) {
                                removeStar({
                                  variables: {
                                    input: {
                                      starrableId: template.objectId,
                                    },
                                  },
                                });
                                return;
                              }

                              addStar({
                                variables: {
                                  input: {
                                    starrableId: template.objectId,
                                  },
                                },
                              });
                            }}
                          >
                            {template.viewerHasStarred ? (
                              <FormattedMessage defaultMessage="Remover dos favoritos" id="yBrZIE" />
                            ) : (
                              <FormattedMessage defaultMessage="Favoritar" id="96qTSK" />
                            )}
                          </MenuActionButtonItem>

                          <MenuActionButtonItem
                            variant="primary"
                            disabled={isAppliedTemplate}
                            onClick={() => {
                              onApplyTemplate?.(isAppliedTemplate ? null : template);

                              if (onToggleSheetOpen) {
                                onToggleSheetOpen();
                              }
                            }}
                          >
                            {isAppliedTemplate ? (
                              <FormattedMessage defaultMessage="Aplicado" id="fpRFEW" />
                            ) : (
                              <FormattedMessage defaultMessage="Aplicar" id="4aS11Q" />
                            )}
                          </MenuActionButtonItem>

                          {inheritedTemplate && !isDefault && canSetAsDefault && (
                            <MenuActionButtonItem
                              variant="primary"
                              disabled={loadingSetAsDefaultFinancialsTemplate}
                              onClick={() => setAsDefaultTemplate()}
                            >
                              <FormattedMessage
                                defaultMessage="Definir como padrão"
                                id="wWS2iq"
                                description="Set template as default button"
                              />
                            </MenuActionButtonItem>
                          )}

                          {isArchived && template.viewerCanDelete && (
                            <MenuActionButtonItem
                              variant="primary"
                              disabled={permanentlyDeleteLoading}
                              onClick={() => {
                                permanentlyDeleteFinancialsTemplate({
                                  variables: {
                                    objectId: template.objectId,
                                  },
                                  onCompleted: onRefetch,
                                });
                              }}
                            >
                              <FormattedMessage defaultMessage="Deletar para sempre" id="MV6/kn" />
                            </MenuActionButtonItem>
                          )}

                          {template.viewerCanChangeVisibility && (
                            <MenuActionButtonItem
                              variant="primary"
                              disabled={changeVisibilityLoading}
                              onClick={() => {
                                changeFinancialsTemplateVisibility({
                                  variables: {
                                    objectId: template.objectId,
                                    input: {
                                      visibility:
                                        template.visibility === IFinancialsTemplateVisibility.Private
                                          ? IFinancialsTemplateVisibility.Public
                                          : IFinancialsTemplateVisibility.Private,
                                    },
                                  },
                                });
                              }}
                            >
                              {isPrivate ? (
                                <FormattedMessage
                                  defaultMessage="Privado"
                                  id="4cmXLc"
                                  description="Template visibility setting"
                                />
                              ) : (
                                <FormattedMessage
                                  description="Template visibility setting"
                                  defaultMessage="Público"
                                  id="//PxJs"
                                />
                              )}
                            </MenuActionButtonItem>
                          )}

                          {template.viewerCanArchive && (
                            <MenuActionButtonItem
                              variant="danger"
                              disabled={archivedOnFinancialsTemplateLoading}
                              onClick={() =>
                                toggleArchivedOnFinancialsTemplate({
                                  variables: {
                                    objectId: template.objectId,
                                  },
                                  onCompleted: onRefetch,
                                })
                              }
                            >
                              {isArchived ? (
                                <FormattedMessage
                                  defaultMessage="Restaurar"
                                  id="LTm6tU"
                                  description="Restore template action"
                                />
                              ) : (
                                <FormattedMessage
                                  defaultMessage="Arquivar"
                                  id="ToNV1d"
                                  description="Archive template action"
                                />
                              )}
                            </MenuActionButtonItem>
                          )}

                          {template.viewerCanCopy && (
                            <MenuActionButtonItem
                              variant="primary"
                              disabled={addFinancialsTemplateLoading}
                              onClick={() => {
                                addFinancialsTemplate({
                                  variables: {
                                    input: {
                                      templateCodes: template.templateCodes,
                                      name: `Cópia de ${template.name}`,
                                      description: template.description,
                                      indicators: template.indicators.map(({ objectId }) => objectId),
                                      vertical: template.vertical,
                                      visibility: IFinancialsTemplateVisibility.Private,
                                      countryCode: template.countryCode,
                                      dataSource: template.dataSource,
                                      betterSuitedFor: [],
                                      overrideCriteria: null,
                                    },
                                  },
                                  onCompleted: onRefetch,
                                });
                              }}
                            >
                              {isMyTemplateTab ? (
                                <FormattedMessage
                                  defaultMessage="Duplicar"
                                  id="QdMuI+"
                                  description="Duplicate template action"
                                />
                              ) : (
                                <FormattedMessage
                                  defaultMessage="Copiar para Meus Templates"
                                  id="bKNtnJ"
                                  description="Copiar template action"
                                />
                              )}
                            </MenuActionButtonItem>
                          )}

                          {!isArchived && template.viewerCanEdit && (
                            <MenuActionButtonItem variant="primary" onClick={closeOnClick(() => onEditClick(template))}>
                              <FormattedMessage defaultMessage="Editar" id="VGHwB5" />
                            </MenuActionButtonItem>
                          )}
                        </Menu>
                      )}
                    </ActionsDropdown>
                  </motion.li>
                );
              })}
            </ul>
          </AnimatePresence>
        )}

        {isLoading && <LoadingSkeleton count={1} />}
      </div>

      {hasNextPage && onFetchMore && (
        <div className="text-center">
          <button
            type="button"
            className="inline-flex font-medium text-blue-600 hover:underline disabled:text-gray-600 disabled:hover:no-underline"
            disabled={isLoading}
            onClick={() => {
              onFetchMore();
            }}
          >
            {isLoading ? (
              <FormattedMessage defaultMessage="Carregando..." id="EMLr/e" />
            ) : (
              <FormattedMessage defaultMessage="Ver mais templates" id="jT3izs" />
            )}
          </button>
        </div>
      )}
    </div>
  );
}
